import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { PageHeader } from "../../components/shared/Primitives";
import EntryModal from "../../components/shared/EntryModal";
import ExcelImportDialog from "../../components/shared/ExcelImportDialog";
import CategoryManager from "../../components/shared/CategoryManager";
import GroupedByCompany from "./GroupedByCompany";
import { Plus, Upload, Search, Download, Settings } from "lucide-react";
import { toast } from "sonner";
import { downloadCSV } from "../../lib/csv";

/**
 * Generic Contacts sub-tab.
 * props:
 *   type: "coi" | "prospect" | "linkedin"
 *   title, eyebrow, dot, description
 *   columns: [{ key, label, render? }]
 *   fields: EntryModal fields
 *   excelColumns: required header names
 *   csvColumns: [{ key, label }]
 *   defaultCategories?: [{ key, color }] (for COI / Prospects)
 *   categoryKind?: "contacts_coi" | "contacts_prospect"
 */
export default function ContactsTab({
  type,
  title,
  eyebrow,
  dot,
  description,
  columns,
  fields,
  excelColumns,
  csvColumns,
  defaultCategories,
  categoryKind,
  groupByCompany = false, // when true, adds a "Group by Company" default-on toggle
}) {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [modal, setModal] = useState({ open: false, entry: null });
  const [importOpen, setImportOpen] = useState(false);
  const [catMgrOpen, setCatMgrOpen] = useState(false);
  const [grouped, setGrouped] = useState(
    () => groupByCompany && (localStorage.getItem(`rw_grp_${type}`) ?? "on") !== "off"
  );
  const seededRef = useRef(false);

  useEffect(() => {
    if (groupByCompany)
      localStorage.setItem(`rw_grp_${type}`, grouped ? "on" : "off");
  }, [grouped, groupByCompany, type]);

  const load = async () => {
    const { data } = await api.get("/contacts", { params: { type, q: q || undefined } });
    setItems(data);
  };

  const loadCategories = async () => {
    if (!categoryKind) return;
    let { data } = await api.get(`/categories/${categoryKind}`);
    if ((!data || data.length === 0) && defaultCategories?.length && !seededRef.current) {
      seededRef.current = true;
      await Promise.all(
        defaultCategories.map((c, i) =>
          api.post(`/categories/${categoryKind}`, { name: c.key, color: c.color, order: i, hidden: false })
        )
      );
      const r = await api.get(`/categories/${categoryKind}`);
      data = r.data;
    }
    const seen = new Set();
    const deduped = [];
    for (const c of data) {
      if (seen.has(c.name)) continue;
      seen.add(c.name);
      deduped.push(c);
    }
    deduped.sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
    setCategories(deduped);
  };

  useEffect(() => {
    load();
    loadCategories();
    // eslint-disable-next-line
  }, [type, q]);

  const save = async (p) => {
    p.type = type;
    try {
      if (p.id) await api.put(`/contacts/${p.id}`, p);
      else await api.post("/contacts", p);
      toast.success("Contact saved");
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail) {
        toast.error(detail.message || "A contact with matching info already exists.");
        // rethrow so EntryModal can keep the dialog open
        throw e;
      }
      throw e;
    }
  };
  const del = async () => {
    if (modal.entry?.id) {
      await api.delete(`/contacts/${modal.entry.id}`);
      toast.success("Contact deleted");
      load();
    }
  };

  // Inject categories into category field options if applicable
  const enrichedFields = useMemo(() => {
    return fields.map((f) =>
      f.name === "category" && categoryKind
        ? { ...f, type: "select", options: categories.map((c) => c.name) }
        : f
    );
  }, [fields, categories, categoryKind]);

  // Build a quick color lookup for category pills
  const catColor = useMemo(() => {
    const m = {};
    for (const c of categories) m[c.name] = c.color;
    return m;
  }, [categories]);

  return (
    <div>
      <PageHeader
        eyebrow={eyebrow}
        title={title}
        dot={dot}
        description={description}
        right={
          <>
            {groupByCompany && (
              <Button
                variant="outline"
                onClick={() => setGrouped((v) => !v)}
                aria-pressed={grouped}
                data-testid={`${type}-group-toggle`}
                className={grouped ? "bg-[var(--rw-surface-2)]" : ""}
              >
                {grouped ? "Grouped: on" : "Grouped: off"}
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => downloadCSV(`${type}-contacts.csv`, items, csvColumns)}
              data-testid={`${type}-export-csv`}
            >
              <Download size={14} className="mr-2" /> Export CSV
            </Button>
            <Button variant="outline" onClick={() => setImportOpen(true)} data-testid={`${type}-import-btn`}>
              <Upload size={14} className="mr-2" /> Import Excel
            </Button>
            {categoryKind && (
              <Button variant="outline" onClick={() => setCatMgrOpen(true)} data-testid={`${type}-categories-btn`}>
                <Settings size={14} className="mr-2" /> Categories
              </Button>
            )}
            <Button
              onClick={() => setModal({ open: true, entry: null })}
              className="bg-[var(--rw-sidebar)] hover:bg-[var(--rw-sidebar-hover)]"
              data-testid={`${type}-add-btn`}
            >
              <Plus size={14} className="mr-2" /> Add {title.replace(/s$/, "")}
            </Button>
          </>
        }
      />
      <div className="p-8">
        <div className="relative mb-4 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <Input
            placeholder="Search…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9"
            data-testid={`${type}-search`}
          />
        </div>
        {groupByCompany && grouped ? (
          <GroupedByCompany
            items={items}
            categories={categories}
            catColor={catColor}
            onClick={(c) => setModal({ open: true, entry: c })}
            type={type}
          />
        ) : (
          <div className="bg-white border border-[var(--rw-border)] rounded-[4px] overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[var(--rw-surface-2)]">
                <tr className="text-left text-[10px] uppercase tracking-[0.08em] font-semibold text-neutral-500">
                  {columns.map((c) => (
                    <th key={c.key} className="py-2 px-4">{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr><td colSpan={columns.length} className="px-4 py-10 text-center text-neutral-400">No contacts yet.</td></tr>
                )}
                {items.map((c) => (
                  <tr
                    key={c.id}
                    className="border-t border-[var(--rw-border)] hover:bg-[var(--rw-surface-2)] cursor-pointer"
                    onClick={() => setModal({ open: true, entry: c })}
                    data-testid={`${type}-row-${c.id}`}
                  >
                    {columns.map((col) => (
                      <td key={col.key} className="py-2 px-4 align-top">
                        {col.render ? col.render(c[col.key], c, { catColor }) : (c[col.key] || "—")}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <EntryModal
        open={modal.open}
        onOpenChange={(v) => setModal({ ...modal, open: v })}
        title={modal.entry ? `Edit ${title.replace(/s$/, "")}` : `New ${title.replace(/s$/, "")}`}
        entry={modal.entry}
        fields={enrichedFields}
        promptAddContact={false}
        attachmentsTargetKind="contact"
        onSave={save}
        onDelete={del}
      />

      <ExcelImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        endpoint={`/contacts/import-excel?type=${type}`}
        columns={excelColumns}
        onDone={load}
      />

      {categoryKind && (
        <CategoryManager
          open={catMgrOpen}
          onOpenChange={setCatMgrOpen}
          kind={categoryKind}
          onChanged={loadCategories}
        />
      )}
    </div>
  );
}
